(function () {
  'use strict';

  /**
   * Custom prev/next buttons + progress bar for the "You may also
   * like" carousel. Does not reimplement any carousel logic.
   *
   * The theme's own ComplementaryProducts class (assets/empire.js,
   * unchanged) creates a Flickity instance internally, but never
   * exposes it globally - window.Flickity does not exist at runtime
   * (verified: the bundle's UMD wrapper resolves as a webpack/
   * CommonJS module internally, so Flickity stays scoped inside
   * empire.js's own closure, not attached to the real window object -
   * Flickity.data() is therefore unreachable from a separately loaded
   * script like this one).
   *
   * Instead of touching empire.js to expose it, this uses things
   * Flickity, unchanged, ALREADY does regardless of that: it still
   * creates the native prev/next buttons in the DOM (hidden via CSS,
   * not removed - see product-redesign.css) with fully working click
   * handlers of their own, and it still positions .flickity-slider via
   * a real, live CSS transform reflecting the actual current scroll
   * position no matter how the carousel is moved (drag, swipe, or its
   * own native buttons). So: clicking a new button here simply
   * forwards a real .click() to the corresponding hidden native button
   * (triggering Flickity's own unmodified handler for it), and the
   * progress bar reads that live transform plus the slider/viewport's
   * own rendered widths - both are pure "observe/forward to what
   * Flickity already provides," not a reimplementation of any carousel
   * logic.
   */

  function initNav(navRoot) {
    if (navRoot.dataset.complementaryNavInitialized === 'true') return false;

    var container = navRoot.closest('.complementary-products');
    var slider = container.querySelector('[data-slider]');
    if (!slider) return false;

    var nativePrev = container.querySelector('.flickity-button.previous');
    var nativeNext = container.querySelector('.flickity-button.next');
    var flickitySlider = container.querySelector('.flickity-slider');
    var flickityViewport = container.querySelector('.flickity-viewport');
    if (!nativePrev || !nativeNext || !flickitySlider || !flickityViewport) {
      return false;
    }

    navRoot.dataset.complementaryNavInitialized = 'true';

    var prevButton = navRoot.querySelector('[data-complementary-prev]');
    var nextButton = navRoot.querySelector('[data-complementary-next]');
    var progressFill = navRoot.querySelector('[data-complementary-progress-fill]');

    if (prevButton) {
      prevButton.addEventListener('click', function () {
        nativePrev.click();
      });
    }
    if (nextButton) {
      nextButton.addEventListener('click', function () {
        nativeNext.click();
      });
    }

    /**
     * Position/progress math, in terms of the same three quantities
     * requested (scrollLeft / scrollWidth / clientWidth), adapted to
     * how Flickity actually positions cells: it uses a CSS transform
     * on .flickity-slider (not native element scrolling), so there is
     * no real .scrollLeft/.scrollWidth to read directly - these are
     * the Flickity-native equivalents instead:
     *   scrollWidth  -> .flickity-slider's own rendered width (the
     *                   full row of all cells+gaps - this alone is
     *                   what makes the whole calculation automatically
     *                   scale with the ACTUAL number of recommended
     *                   products returned by Shopify: more products
     *                   simply renders a wider total here, nothing
     *                   about that number is hard-coded)
     *   clientWidth  -> .flickity-viewport's own rendered width (the
     *                   visible window - this is what makes "visible
     *                   product capacity" automatically follow from
     *                   however many cards actually fit at the
     *                   current viewport width, not a hard-coded 6.3)
     *   scrollLeft   -> how far the first product card has moved off
     *                   the viewport's left edge (0 at the very start)
     * progress = (scrollLeft + clientWidth) / scrollWidth, clamped to
     * [0,1] - at rest (scrollLeft=0) this is clientWidth/scrollWidth,
     * i.e. exactly "how much of the total set is visible right now",
     * not a fixed 50%/0% starting value. Flickity's own contain:true
     * option (already set in ComplementaryProducts' init, unchanged)
     * guarantees the carousel can never scroll past the point where
     * scrollLeft+clientWidth==scrollWidth, so the true end of the
     * recommendation set is exactly where this reaches 100% (verified
     * live: at rest this computes to exactly clientWidth/scrollWidth,
     * matching this measurement approach against the real page).
     *
     * scrollLeft isn't read from .flickity-slider's own transform:
     * Flickity actually gives each CELL its own individual percentage
     * transform (not one shared slider-level offset representing pure
     * scroll distance), and with wrapAround enabled (unchanged, from
     * ComplementaryProducts' init options) those per-cell values and
     * the slider's own transform interact in a way that doesn't reduce
     * to a simple "percent of total width" - confirmed live: magnitudes
     * exceeding 100% and non-monotonic values were observed when
     * treating the slider's transform this way. Reading the first
     * cell's actual on-screen position instead sidesteps needing to
     * understand that internal representation at all - it directly
     * reflects the real combined result of everything Flickity applies.
     */
    function getSliderWidth() {
      // .flickity-slider's own getBoundingClientRect().width is NOT
      // the true "scrollWidth" here - its children ([data-slide]) are
      // position:absolute, so they don't contribute to the slider
      // element's own intrinsic size the way normal-flow content
      // would; the slider box just renders at whatever CSS gives it
      // (matching the viewport width). The real total scrollable span
      // has to be summed from the cells themselves instead - this is
      // what makes the total automatically reflect the actual number
      // of recommended products (more cells = larger sum), not a
      // fixed/hard-coded count.
      var cells = container.querySelectorAll('[data-slide]');
      var total = 0;
      cells.forEach(function (cell) {
        var cs = getComputedStyle(cell);
        total +=
          cell.getBoundingClientRect().width +
          parseFloat(cs.marginLeft || 0) +
          parseFloat(cs.marginRight || 0);
      });
      return total;
    }

    function getScrollLeft() {
      var firstCell = container.querySelector('[data-slide]');
      if (!firstCell) return 0;
      var viewportLeft = flickityViewport.getBoundingClientRect().left;
      var firstCellLeft = firstCell.getBoundingClientRect().left;
      return viewportLeft - firstCellLeft;
    }

    function updateProgress() {
      if (!progressFill) return;

      var sliderWidth = getSliderWidth();
      var viewportWidth = flickityViewport.getBoundingClientRect().width;
      if (!sliderWidth || !viewportWidth) return;

      var scrollLeft = getScrollLeft();
      var percent = ((scrollLeft + viewportWidth) / sliderWidth) * 100;
      percent = Math.max(0, Math.min(100, percent));
      progressFill.style.width = percent + '%';
    }

    // Flickity updates style/transform on the slider AND on individual
    // cells on every position change (drag, swipe, or either native
    // button) - subtree:true catches both, so this stays accurate
    // regardless of which element Flickity happens to touch for a
    // given move. A resize listener covers the case where the
    // viewport/cell widths themselves change without a position update.
    var sliderObserver = new MutationObserver(updateProgress);
    sliderObserver.observe(flickitySlider, {
      attributes: true,
      attributeFilter: ['style'],
      subtree: true,
    });
    window.addEventListener('resize', updateProgress);

    updateProgress();
    return true;
  }

  function findAndInitNav(root) {
    var navRoot = root.querySelector('[data-complementary-nav]');
    return navRoot ? initNav(navRoot) : false;
  }

  document.querySelectorAll('[data-complementary-products]').forEach(function (root) {
    // Recommendations load in asynchronously (ComplementaryProducts'
    // own AJAX fetch, unchanged) - the nav markup and Flickity's own
    // generated elements only exist once that swaps in
    // root.innerHTML, so watch for it rather than assuming it's
    // already there.
    if (findAndInitNav(root)) return;

    var observer = new MutationObserver(function () {
      if (findAndInitNav(root)) observer.disconnect();
    });
    observer.observe(root, { childList: true, subtree: true });
  });
})();
